import React, { useState } from 'react';
import {
	Button,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
} from '@mui/material';
import { appVerdictName, VerdictStatus } from '../../../util/enumerations';
import { VerdictDTO } from '../../../DTO/VerdictDTO';
import { useUpdateApplication } from '..';
import { APIService } from '../../../services/apiService';
import { setVerdictBorderStyle } from '../util';
import CourseMappingsGrid from '../../CourseMappingsGrid';
import VerdictHistory from './VerdictHistory';
import { CourseDTO } from '../../../DTO/CourseDTO';

type CreateVerdictProps = {
	verdict: VerdictDTO | null;
	setVerdict: React.Dispatch<React.SetStateAction<VerdictDTO | null>>;
	rowIndex: number;
	coursesHome: CourseDTO[];
	coursesExchange: CourseDTO[];
	exchangeSchoolId: number;
};
const api = new APIService();

export const CreateVerdict = React.memo(
	({ verdict, setVerdict, rowIndex, coursesHome, coursesExchange, exchangeSchoolId }: CreateVerdictProps) => {
		const [showHistory, setShowHistory] = useState(false);
		const [showMappings, setShowMappings] = useState(false);
		const [mappingType, setMappingType] = useState<'home' | 'exchange'>('home');
		const { giveVerdict } = useUpdateApplication();

		const getFilteredCourseIds = (type: 'home' | 'exchange') => {
			if (type === 'home') {
				// Filter to only include home (KTH) courses
				return coursesHome.map((c) => c.id);
			} else {
				// Filter to only include exchange (non-KTH) courses  
				return coursesExchange.map((c) => c.id);
			}
		};

		const saveVerdict = React.useCallback(() => {
			if (!verdict) return;
			api.saveVerdict(verdict)
				.then((data) => {
					setVerdict(null);
					giveVerdict(rowIndex, data);
				})
				.catch((ex) => console.log(ex));
		}, [verdict, rowIndex, giveVerdict]);

		return React.useMemo(
			() =>
				verdict ? (
					<div>
						<div
							style={{
								border: setVerdictBorderStyle(verdict.status),
								padding: '10px',
							}}
						>
							<strong>Verdict</strong>
							<br />
							<FormLabel
								style={{ marginTop: '10px' }}
								component="legend"
							>
								Status
							</FormLabel>
							<RadioGroup
								aria-label="verdictStatus"
								name="status"
								value={verdict?.status}
								onChange={(e) =>
									verdict &&
									setVerdict({
										...verdict,
										status: Number(e.target.value),
									})
								}
							>
								<FormControlLabel
									value="1"
									control={<Radio />}
									label={appVerdictName(
										VerdictStatus.Accepted
									)}
									checked={
										verdict.status ===
										VerdictStatus.Accepted
									}
								/>
								<FormControlLabel
									value="2"
									control={<Radio />}
									label={appVerdictName(
										VerdictStatus.AcceptedUnderSpecialCircumstances
									)}
									checked={
										verdict?.status ===
										VerdictStatus.AcceptedUnderSpecialCircumstances
									}
								/>
								<FormControlLabel
									value="3"
									control={<Radio />}
									label={appVerdictName(
										VerdictStatus.Rejected
									)}
									checked={
										verdict.status ===
										VerdictStatus.Rejected
									}
								/>
							</RadioGroup>
							<br />
							<TextField
								id="outlined-multiline-static"
								style={{ width: '100%' }}
								label="Note"
								multiline
								rows={4}
								placeholder="Note..."
								variant="outlined"
								value={verdict.note}
								onChange={(e) =>
									setVerdict({
										...verdict,
										note: e.target.value,
									})
								}
							/>
							<br />
							<Button
								variant="contained"
								color="primary"
								style={{ marginTop: '10px' }}
								size="small"
								disabled={verdict.status < 1}
								onClick={() => saveVerdict()}
							>
								Save
							</Button>
							<Button
								variant="contained"
								color="secondary"
								style={{
									marginLeft: '10px',
									marginTop: '10px',
								}}
								size="small"
								onClick={() => setShowHistory(prevShowHistory => !prevShowHistory)}
							>
								{showHistory ? "Hide verdicts" :  "Show verdicts"}
							</Button>
							<Button
								variant="contained"
								color="secondary"
								style={{
									marginLeft: '10px',
									marginTop: '10px',
								}}
								size="small"
								onClick={() => setShowMappings(prevShowMappings => !prevShowMappings)}
							>
								{showMappings ? "Hide mappings" :  "Show mappings"}
							</Button>
						</div>
						<br />
						{showHistory ? (
							<VerdictHistory
								courseMappingHeadId={verdict.courseMappingHeadId}
								verdict={verdict}
								setVerdict={setVerdict}
								height='400px'
							/>
						) : null}
						{showMappings ? (
							<div style={{ marginTop: '10px' }}>
								{/* Radio button section */}
								<div style={{ marginBottom: '10px' }}>
									<FormControl component="fieldset">
										<RadioGroup
											row
											value={mappingType}
											onChange={(e) => setMappingType(e.target.value as 'home' | 'exchange')}
										>
											<FormControlLabel 
												value="home" 
												control={<Radio />} 
												label="Home mappings" 
											/>
											<FormControlLabel 
												value="exchange" 
												control={<Radio />} 
												label="Exchange mappings" 
											/>
										</RadioGroup>
									</FormControl>
								</div>
								
								{/* Single mapping grid with filtered courseIds */}
								<CourseMappingsGrid 
									height="400px" 
									minimalistic={true} 
									schoolId={exchangeSchoolId}
									courseIds={getFilteredCourseIds(mappingType)}
								/>
							</div>
						) : null}
					</div>
				) : null,
			[
				verdict,
				saveVerdict,
				showHistory,
				showMappings,
				mappingType,
				coursesHome,
				coursesExchange,
				exchangeSchoolId
			]
		);
	}
);
